import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Shield, LogOut, Key, Smartphone } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * /settings/security — change password + device session management.
 * Minimal V1 per directive:
 *  - Change password (bumps session_version → signs out all other devices)
 *  - Active devices list with "Sign out all other devices"
 */
export default function Security() {
  const navigate = useNavigate();

  const [devices, setDevices] = useState([]);
  const [loadingDevices, setLoadingDevices] = useState(true);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changingPw, setChangingPw] = useState(false);
  const [revoking, setRevoking] = useState(false);

  const authHeaders = () => {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  useEffect(() => {
    // Guard: ask the server directly whether we're authenticated via cookies.
    // Avoids a race where user context hasn't loaded yet.
    let cancelled = false;
    (async () => {
      try {
        await axios.get(`${API}/api/auth/me`, {
          headers: authHeaders(),
          withCredentials: true,
        });
        if (!cancelled) loadDevices();
      } catch {
        if (!cancelled) {
          navigate('/auth?mode=signin', { replace: true });
        }
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadDevices = async () => {
    setLoadingDevices(true);
    try {
      const res = await axios.get(`${API}/api/auth/devices`, {
        headers: authHeaders(),
        withCredentials: true,
      });
      setDevices(res.data?.devices || []);
    } catch {
      setDevices([]);
    } finally {
      setLoadingDevices(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (newPassword.length < 8) {
      toast.error('New password must be at least 8 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    setChangingPw(true);
    try {
      const res = await axios.post(
        `${API}/api/auth/change-password`,
        { current_password: currentPassword, new_password: newPassword },
        { headers: authHeaders(), withCredentials: true }
      );
      toast.success(res.data?.message || 'Password changed');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      localStorage.removeItem('token'); // legacy Bearer token is now invalid; cookies are re-issued
      loadDevices();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not change password');
    } finally {
      setChangingPw(false);
    }
  };

  const handleRevokeOthers = async () => {
    setRevoking(true);
    try {
      await axios.post(
        `${API}/api/auth/devices/sign-out-others`,
        {},
        { headers: authHeaders(), withCredentials: true }
      );
      toast.success('Other devices signed out');
      localStorage.removeItem('token');
      loadDevices();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to sign out other devices');
    } finally {
      setRevoking(false);
    }
  };

  const fmt = (iso) => {
    try {
      return new Date(iso).toLocaleString();
    } catch {
      return iso;
    }
  };

  const otherCount = devices.filter((d) => !d.current).length;

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--fy-background-primary)' }} data-testid="security-page">
      <header
        className="sticky top-0 z-40"
        style={{ backgroundColor: 'var(--fy-background-primary)', borderBottom: '1px solid var(--fy-border-subtle)' }}
      >
        <div className="max-w-lg mx-auto px-4">
          <div className="flex items-center h-14 gap-3">
            <button
              onClick={() => navigate('/settings')}
              className="p-2 -ml-2 rounded-lg hover:opacity-70 transition-opacity"
              data-testid="security-back"
            >
              <ArrowLeft className="w-5 h-5" style={{ color: 'var(--fy-text-primary)' }} />
            </button>
            <h1 className="text-lg font-bold" style={{ color: 'var(--fy-text-primary)' }}>
              Security
            </h1>
          </div>
        </div>
      </header>

      <main className="max-w-lg mx-auto px-4 py-6 space-y-6">
        {/* Change Password */}
        <Card
          className="p-5"
          style={{ backgroundColor: 'var(--fy-surface-muted)', border: '1px solid var(--fy-border-subtle)' }}
          data-testid="change-password-card"
        >
          <div className="flex items-center gap-2 mb-4">
            <Key className="w-4 h-4" style={{ color: 'var(--lk-sage)' }} />
            <h2 className="font-semibold" style={{ color: 'var(--fy-text-primary)' }}>
              Change password
            </h2>
          </div>
          <form onSubmit={handleChangePassword} className="space-y-3">
            <Input
              type="password"
              placeholder="Current password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              required
              data-testid="current-password-input"
              className="h-11 rounded-xl"
            />
            <Input
              type="password"
              placeholder="New password (min 8 chars)"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              minLength={8}
              data-testid="new-password-input"
              className="h-11 rounded-xl"
            />
            <Input
              type="password"
              placeholder="Confirm new password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              data-testid="confirm-password-input"
              className="h-11 rounded-xl"
            />
            <p className="text-xs" style={{ color: 'var(--fy-text-secondary)' }}>
              Changing your password signs out every other device you’re signed into.
            </p>
            <Button
              type="submit"
              disabled={changingPw}
              className="w-full h-11 rounded-xl font-medium"
              style={{ backgroundColor: '#7A8F5B', color: '#FFFFFF' }}
              data-testid="submit-change-password"
            >
              {changingPw ? 'Changing…' : 'Change password'}
            </Button>
          </form>
        </Card>

        {/* Devices */}
        <Card
          className="p-5"
          style={{ backgroundColor: 'var(--fy-surface-muted)', border: '1px solid var(--fy-border-subtle)' }}
          data-testid="devices-card"
        >
          <div className="flex items-center gap-2 mb-4">
            <Smartphone className="w-4 h-4" style={{ color: 'var(--lk-sage)' }} />
            <h2 className="font-semibold" style={{ color: 'var(--fy-text-primary)' }}>
              Your devices
            </h2>
          </div>

          {loadingDevices ? (
            <p className="text-sm" style={{ color: 'var(--fy-text-secondary)' }}>
              Loading…
            </p>
          ) : devices.length === 0 ? (
            <p className="text-sm" style={{ color: 'var(--fy-text-secondary)' }}>
              No active sessions.
            </p>
          ) : (
            <ul className="space-y-2 mb-4" data-testid="devices-list">
              {devices.map((d) => (
                <li
                  key={d.session_id}
                  className="flex items-center justify-between py-2"
                  style={{ borderBottom: '1px solid var(--fy-border-subtle)' }}
                  data-testid={`device-row-${d.session_id}`}
                >
                  <div>
                    <p className="text-sm font-medium" style={{ color: 'var(--fy-text-primary)' }}>
                      {d.device_label || 'Browser'}
                      {d.current && (
                        <span
                          className="ml-2 text-xs px-2 py-0.5 rounded-full"
                          style={{ backgroundColor: 'var(--lk-sage)', color: '#FFFFFF' }}
                          data-testid="current-device-badge"
                        >
                          This device
                        </span>
                      )}
                    </p>
                    <p className="text-xs" style={{ color: 'var(--fy-text-secondary)' }}>
                      Last active {fmt(d.last_seen_at)}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          )}

          <Button
            onClick={handleRevokeOthers}
            disabled={revoking || otherCount === 0}
            variant="outline"
            className="w-full h-11 rounded-xl font-medium"
            data-testid="sign-out-others-btn"
          >
            <LogOut className="w-4 h-4 mr-2" />
            {otherCount === 0
              ? 'No other devices signed in'
              : `Sign out ${otherCount} other device${otherCount === 1 ? '' : 's'}`}
          </Button>
        </Card>

        <div
          className="text-xs flex items-start gap-2 px-1"
          style={{ color: 'var(--fy-text-secondary)' }}
        >
          <Shield className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
          <span>
            Sign-in sessions use secure HttpOnly cookies. We never store your password in the browser.
          </span>
        </div>
      </main>
    </div>
  );
}
